import React, { Component } from "react";
import ReactDOM from "react-dom/client";
import "./i18n";
import "./index.scss";
import App from "./App";
import reportWebVitals from "./reportWebVitals";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

// Ignore the known, non-fatal ToastContainer warning; surface everything else.
const isIgnorableError = (error: unknown): boolean => {
  const message = (error as { message?: unknown })?.message;
  return typeof message === "string" && message.includes("ToastContainer");
};

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State | null {
    // Returning null keeps rendering children (used for ignorable errors).
    return isIgnorableError(error) ? null : { hasError: true };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    if (isIgnorableError(error)) return;
    console.error("Uncaught error rendered a fallback:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "48px 24px", textAlign: "center" }}>
          <h3>Something went wrong.</h3>
          <p>Please reload the page. If it keeps happening, try again later.</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 12,
              padding: "10px 20px",
              borderRadius: 6,
              border: "1px solid #ccc",
              cursor: "pointer",
            }}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
